import React, { useEffect, useMemo, useState } from "react";
import { useAuthModals } from "./useAuthModals";
import { modalButtonClasses } from "./modalClasses";
import { AuthModalHost } from "./components/AuthModalHost";
import { LoginFormCard } from "./components/LoginFormCard";
import { useLoginFormState } from "./useLoginFormState";

export const LoginExperience = ({ initialModal = null, resetModal = null } = {}) => {
  const loginState = useLoginFormState();
  const {
    activeModal,
    openModal,
    closeModal,
    isModalOpen,
    isRegisterModal,
    isVerificationModal,
    showVerificationModal,
    handleCloseResetModal,
    handleResetSuccess,
  } = useAuthModals({ initialModal, resetModal });

  const [verificationEmail, setVerificationEmail] = useState("");

  useEffect(() => {
    if (activeModal === "register" || activeModal === "verify") {
      loginState.clearAuthError();
    }
  }, [activeModal, loginState]);

  const handleShowVerificationModal = (email) => {
    loginState.clearAuthError();
    setVerificationEmail(email || "");
    showVerificationModal();
  };

  const modalProps = useMemo(
    () => ({
      isModalOpen,
      activeModal,
      isRegisterModal,
      isVerificationModal,
      closeModal,
      openModal,
      handleCloseResetModal,
      handleResetSuccess,
      verificationEmail,
      resetModal,
      onShowVerificationModal: handleShowVerificationModal,
    }),
    [
      isModalOpen,
      activeModal,
      isRegisterModal,
      isVerificationModal,
      closeModal,
      openModal,
      handleCloseResetModal,
      handleResetSuccess,
      verificationEmail,
      resetModal,
    ],
  );

  const primaryButtonClasses = modalButtonClasses.primary;
  const googleButtonClasses = modalButtonClasses.google;
  const googleIconWrapperClasses = modalButtonClasses.googleIconWrapper;

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <GradientBackground />

      <div className="relative flex min-h-screen items-center justify-center px-4 py-8 md:px-6 md:py-10 transform origin-top scale-[0.89]">
        <div className="w-full max-w-4xl">
          <LoginFormCard
            emailInput={loginState.emailInput}
            onEmailChange={loginState.setEmailInput}
            passwordInput={loginState.passwordInput}
            onPasswordChange={loginState.setPasswordInput}
            showPassword={loginState.showPassword}
            onTogglePassword={loginState.togglePasswordVisibility}
            onSubmit={loginState.handleSubmit}
            loading={loginState.loadingAuth}
            authError={loginState.authError}
            onForgotPassword={() => openModal("forgot")}
            onRegister={() => openModal("register")}
            onGoogleSignIn={loginState.loginWithGoogle}
            onGoogleError={loginState.handleGoogleError}
            primaryButtonClasses={primaryButtonClasses}
            googleButtonClasses={googleButtonClasses}
            googleIconWrapperClasses={googleIconWrapperClasses}
          />
        </div>
      </div>

      <AuthModalHost {...modalProps} />
    </div>
  );
};

const GradientBackground = () => (
  <div className="absolute inset-0">
    <div className="pointer-events-none absolute -top-40 left-32 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
    <div className="pointer-events-none absolute bottom-10 right-10 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
  </div>
);
